import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pageTitle } from '@/lib/page-title';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bookmark, BookmarkCheck, Info, Share2, Sparkles, Volume2, VolumeX, X } from 'lucide-react';
import { REEL_TABS, reelFor, type ReelItem, type ReelTab, savedRecipes, toggleSavedRecipe } from '@/lib/reel';
import { trackEvent, trackPageView } from '@/lib/analytics';
import { getQuickToolsConfig, money, type QuickToolConfig, type QuickToolKey } from '@/lib/quicktools-api';
import { cn } from '@/lib/utils';
import { ReelSheet } from '@/features/reel/ReelSheet';
import { ShareCard } from '@/features/reel/ShareCard';
import { costLabel, freeLabel } from '@/features/reel/labels';
import { useReelCosts } from '@/features/reel/useReel';

// THE REEL (Studio Blueprint 03 "Watch", 05 "spec plate"): one real output
// at a time, full screen and vertical, the stages as tabs across the top, a
// rail (Details, Save, Share) and one button, "Make one like this".
//
//   next / previous     swipe up or down · J K or the arrow keys
//   change stage        swipe the tab row · [ ] or 1-6
//   make one like this  the button · M or Enter (lifts the recipe sheet)
//   back to watching    drag the sheet down, Android Back · Esc
//   save a recipe       long-press the video · S
//
// The sheet, Details and Share are #hash history entries (2026-10-16), so
// Android Back closes them; the clip under them pauses where it is and plays
// on from the same second. Only the clip on screen plays; the next one
// preloads. ?item=<id> follows what is on screen, so a copied URL opens it.

type Overlay = 'make' | 'details' | 'share' | null;
const overlayOf = (hash: string): Overlay => {
  const h = hash.replace('#', '');
  return h === 'make' || h === 'details' || h === 'share' ? h : null;
};
const typing = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(el.tagName));

export default function Watch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const tabParam = params.get('tab') as ReelTab | null;
  const tab: ReelTab = tabParam && (REEL_TABS as readonly string[]).includes(tabParam) ? tabParam : 'all';
  const items = useMemo(() => reelFor(tab), [tab]);
  const overlay = overlayOf(location.hash);
  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [saved, setSaved] = useState<string[]>(() => savedRecipes());
  const [notice, setNotice] = useState<string | null>(null);
  const [quick, setQuick] = useState<Partial<Record<QuickToolKey, QuickToolConfig>>>({});
  const costs = useReelCosts();
  const scroller = useRef<HTMLDivElement>(null);
  const videos = useRef<(HTMLVideoElement | null)[]>([]);
  const item: ReelItem | undefined = items[index];

  useEffect(() => {
    trackPageView('watch', { tab });
  }, [tab]);

  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(`${t('reel.metaTitle')} | FaceShot-ChopShop`);
    return () => {
      document.title = previous;
    };
  }, [t]);

  useEffect(() => {
    getQuickToolsConfig().then(setQuick).catch(() => undefined);
  }, []);

  /** The plate's price: live credits for the studios, the tool's price for quick tools. */
  const priceOf = useCallback((it: ReelItem): string | null => {
    if (it.studio === 'quick') {
      const c = quick[it.key as QuickToolKey];
      return c ? t('reel.cost.perFile', { price: money(c.price_cents) }) : null;
    }
    return costLabel(costs.data?.[it.studio], t);
  }, [quick, costs.data, t]);

  // ?item=<id> (from home's Made-here strip, or a shared link) opens on that item.
  useEffect(() => {
    const wanted = params.get('item');
    const i = wanted ? items.findIndex((x) => x.id === wanted) : -1;
    const el = scroller.current;
    if (i > 0 && el) el.scrollTo({ top: i * el.clientHeight, behavior: 'instant' as ScrollBehavior });
    setIndex(Math.max(0, i));
    // Once per tab; later scrolling moves the index.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // The item in view plays; every other one pauses.
  useEffect(() => {
    const el = scroller.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio > 0.6) setIndex(Number((e.target as HTMLElement).dataset.i));
        }
      },
      { root: el, threshold: [0.6] },
    );
    el.querySelectorAll('[data-i]').forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [items]);

  // A panel pauses the clip where it is; closing it plays on from there.
  useEffect(() => {
    videos.current.forEach((v, i) => {
      if (!v) return;
      if (i === index && !overlay) {
        v.muted = muted;
        void v.play().catch(() => undefined);
      } else {
        v.pause();
      }
    });
  }, [index, muted, items, overlay]);

  // The address follows the item on screen.
  useEffect(() => {
    if (!item || overlay || params.get('item') === item.id) return;
    const q = new URLSearchParams(location.search);
    q.set('item', item.id);
    navigate({ pathname: location.pathname, search: `?${q}` }, { replace: true });
  }, [item, overlay, params, location.search, location.pathname, navigate]);

  const go = useCallback(
    (i: number) => {
      const el = scroller.current;
      if (!el) return;
      const next = Math.max(0, Math.min(items.length - 1, i));
      el.scrollTo({ top: next * el.clientHeight, behavior: 'smooth' });
    },
    [items.length],
  );

  const setTab = useCallback(
    (next: ReelTab) => {
      navigate({ pathname: location.pathname, search: next === 'all' ? '' : `?tab=${next}` }, { replace: true });
      scroller.current?.scrollTo({ top: 0 });
    },
    [navigate, location.pathname],
  );

  const open = useCallback(
    (o: Exclude<Overlay, null>) => {
      if (o === 'make' && item) trackEvent('reel_sheet_open', { item: item.id, studio: item.studio });
      navigate({ pathname: location.pathname, search: location.search, hash: `#${o}` }, { state: { reelOverlay: true } });
    },
    [navigate, location.pathname, location.search, item],
  );

  const close = useCallback(() => {
    if ((location.state as { reelOverlay?: boolean } | null)?.reelOverlay) navigate(-1);
    else navigate({ pathname: location.pathname, search: location.search }, { replace: true });
  }, [location.state, location.pathname, location.search, navigate]);

  const save = useCallback(() => {
    if (!item) return;
    const now = toggleSavedRecipe(item.id);
    setSaved(savedRecipes());
    setNotice(now ? t('reel.savedToast') : t('reel.unsavedToast'));
    window.setTimeout(() => setNotice(null), 1800);
  }, [item, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      if (overlay) {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
        return;
      }
      const ti = REEL_TABS.indexOf(tab);
      if (e.key === 'j' || e.key === 'J' || e.key === 'ArrowDown') { e.preventDefault(); go(index + 1); }
      else if (e.key === 'k' || e.key === 'K' || e.key === 'ArrowUp') { e.preventDefault(); go(index - 1); }
      else if (e.key === ']') setTab(REEL_TABS[Math.min(REEL_TABS.length - 1, ti + 1)]);
      else if (e.key === '[') setTab(REEL_TABS[Math.max(0, ti - 1)]);
      else if (/^[1-6]$/.test(e.key)) setTab(REEL_TABS[Number(e.key) - 1]);
      else if (e.key === 'm' || e.key === 'M' || (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement))) {
        e.preventDefault();
        open('make');
      } else if (e.key === 's' || e.key === 'S') { e.preventDefault(); save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, index, overlay, close, open, save, setTab, tab]);

  // Swipe the tab row to change stage.
  const tabSwipe = useRef<number | null>(null);
  const onTabsUp = (e: React.PointerEvent) => {
    if (tabSwipe.current == null) return;
    const dx = e.clientX - tabSwipe.current;
    tabSwipe.current = null;
    if (Math.abs(dx) < 48) return;
    const ti = REEL_TABS.indexOf(tab);
    setTab(REEL_TABS[Math.max(0, Math.min(REEL_TABS.length - 1, ti + (dx < 0 ? 1 : -1)))]);
  };

  // Long-press saves the recipe.
  const press = useRef<number | null>(null);
  const pressStart = () => { press.current = window.setTimeout(() => { press.current = null; save(); }, 600); };
  const pressEnd = () => { if (press.current) { window.clearTimeout(press.current); press.current = null; } };

  const railBtn = 'flex min-h-[56px] w-[4.25rem] flex-col items-center justify-center gap-1 rounded-2xl bg-black/40 text-[11px] font-semibold text-white ring-1 ring-white/15 backdrop-blur-md transition hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

  return (
    <div className="fixed inset-0 z-30 bg-black text-white" data-testid="watch">
      {/* Top: back to the site, and the stages. */}
      <div className="absolute inset-x-0 top-0 z-20 bg-gradient-to-b from-black/80 to-transparent pb-6 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="mx-auto flex max-w-xl items-center gap-2 px-3">
          <Link to="/" className="flex h-10 shrink-0 items-center gap-1 rounded-full bg-white/10 px-3 text-[13px] font-semibold" aria-label={t('reel.back')}>
            <ArrowLeft className="h-4 w-4" aria-hidden /> {t('shell.tabs.home')}
          </Link>
          <div
            role="tablist"
            aria-label={t('reel.stages')}
            onPointerDown={(e) => { tabSwipe.current = e.clientX; }}
            onPointerUp={onTabsUp}
            className="flex flex-1 touch-pan-y gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {REEL_TABS.map((key) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                data-testid={`watch-tab-${key}`}
                className={cn('h-9 shrink-0 rounded-full px-3 text-[13px] font-semibold transition-colors', tab === key ? 'bg-white text-black' : 'text-white/75 hover:text-white')}
              >
                {t(`reel.tabs.${key}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={scroller} className="h-[100dvh] snap-y snap-mandatory overflow-y-auto overscroll-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((it, i) => {
          const price = priceOf(it);
          return (
            <section key={`${tab}-${it.id}`} data-i={i} className="relative flex h-[100dvh] snap-start items-center justify-center" aria-label={t(`reel.items.${it.key}`)}>
              <div
                className="relative h-full w-full max-w-xl"
                onPointerDown={pressStart}
                onPointerUp={pressEnd}
                onPointerLeave={pressEnd}
                onPointerCancel={pressEnd}
                onContextMenu={(e) => e.preventDefault()}
              >
                {it.kind === 'video' ? (
                  <video
                    ref={(v) => {
                      videos.current[i] = v;
                    }}
                    src={it.src}
                    poster={it.poster}
                    muted={muted}
                    loop
                    playsInline
                    preload={i === index || i === index + 1 ? 'auto' : 'none'}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <img src={it.src} alt={t(`reel.items.${it.key}`)} loading={i === 0 ? 'eager' : 'lazy'} className="h-full w-full object-contain" />
                )}
                {/* The spec plate: facts from the file itself, and what it costs to make one. */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent px-4 pb-[calc(var(--tabbar-h)+env(safe-area-inset-bottom)+1rem)] pt-16 md:pb-8">
                  <p className="pr-[5rem] text-[17px] font-bold leading-snug">{t(`reel.items.${it.key}`)}</p>
                  <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 pr-[5rem] font-mono text-[11px] uppercase tracking-[0.12em] text-white/70" data-testid="watch-plate">
                    <div className="flex gap-1.5"><dt>{t('reel.plate.studio')}</dt><dd className="text-white">{t(`studios.${it.studio}.name`)}</dd></div>
                    {it.seconds && <div className="flex gap-1.5"><dt>{t('reel.plate.clip')}</dt><dd className="text-white">{t('reel.plate.seconds', { n: it.seconds })}</dd></div>}
                    {price && <div className="flex gap-1.5"><dt>{t('reel.plate.cost')}</dt><dd className="tabular-nums text-white">{price}</dd></div>}
                    <div className="flex gap-1.5"><dt>{t('reel.plate.made')}</dt><dd className="text-white">{t('reel.plate.here')}</dd></div>
                  </dl>
                  <div className="mt-1 h-5 text-xs font-semibold text-accent" aria-live="polite">{i === index ? notice : null}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => open('make')}
                      data-testid="reel-make"
                      className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-2xl border border-accent/50 bg-accent/20 px-4 text-[16px] font-bold text-white"
                    >
                      <Sparkles className="h-4 w-4 text-accent" aria-hidden /> {t('reel.make')}
                    </button>
                    {it.kind === 'video' && (
                      <button
                        type="button"
                        onClick={() => setMuted((m) => !m)}
                        className="flex min-h-[48px] items-center gap-1.5 rounded-2xl bg-white/10 px-3 text-[13px] font-semibold"
                      >
                        {muted ? <VolumeX className="h-4 w-4" aria-hidden /> : <Volume2 className="h-4 w-4" aria-hidden />}
                        {muted ? t('reel.soundOn') : t('reel.soundOff')}
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-center text-[11.5px] text-white/60">{t('reel.free')}</p>
                  <p className="mt-1 hidden text-center text-[11px] text-white/45 md:block">{t('reel.keysHint')}</p>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* The rail. */}
      {item && (
        <div className="pointer-events-none absolute inset-x-0 z-20 mx-auto flex max-w-xl justify-end px-3" style={{ bottom: 'calc(var(--tabbar-h, 0px) + env(safe-area-inset-bottom) + 10rem)' }}>
          <div className="pointer-events-auto flex flex-col gap-2" data-testid="watch-rail">
            <button type="button" className={railBtn} onClick={() => open('details')} data-testid="reel-details">
              <Info className="h-5 w-5" aria-hidden />{t('reel.rail.details')}
            </button>
            <button type="button" className={railBtn} onClick={save} aria-pressed={saved.includes(item.id)} data-testid="reel-save">
              {saved.includes(item.id) ? <BookmarkCheck className="h-5 w-5 text-accent" aria-hidden /> : <Bookmark className="h-5 w-5" aria-hidden />}
              {saved.includes(item.id) ? t('reel.rail.saved') : t('reel.rail.save')}
            </button>
            <button type="button" className={railBtn} onClick={() => open('share')} data-testid="reel-share">
              <Share2 className="h-5 w-5" aria-hidden />{t('reel.rail.share')}
            </button>
          </div>
        </div>
      )}

      {item && overlay === 'make' && (
        <ReelSheet item={item} cost={item.studio === 'quick' ? undefined : costs.data?.[item.studio]} price={priceOf(item)} onClose={close} />
      )}

      {item && (overlay === 'details' || overlay === 'share') && createPortal(
        <div className="fixed inset-0 z-[70]" data-testid={`watch-panel-${overlay}`}>
          <button type="button" tabIndex={-1} aria-label={t('reel.sheet.backToVideo')} className="absolute inset-0 bg-black/40" onClick={close} />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="watch-panel-title"
            className="absolute inset-x-0 bottom-0 mx-auto max-h-[85dvh] max-w-xl overflow-y-auto rounded-t-[1.6rem] border border-white/10 bg-[hsl(219_22%_10%)] p-5 text-foreground"
            style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 id="watch-panel-title" className="font-display text-xl font-bold">
                {overlay === 'details' ? t('reel.rail.details') : t('reel.share.title')}
              </h2>
              <button type="button" onClick={close} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-3 text-sm font-semibold hover:border-accent/50">
                <X className="h-4 w-4" aria-hidden /> {t('reel.sheet.backToVideo')}
              </button>
            </div>
            {overlay === 'details' ? (
              <div data-testid="watch-details-panel">
                <p className="font-semibold">{t(`reel.items.${item.key}`)}</p>
                <p className="mt-1 text-sm text-muted-foreground">{t(`reel.recipeText.${item.key}`)}</p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  {([
                    [t('reel.sheet.studio'), t(`studios.${item.studio}.name`)],
                    [t('reel.sheet.format'), t(`reel.recipes.${item.key}.format`)],
                    [t('reel.sheet.length'), t(`reel.recipes.${item.key}.length`)],
                    ...(item.style ? [[t('reel.sheet.style'), t(`corrido.styles.${item.style}`)]] : []),
                    ...(item.seconds ? [[t('reel.plate.clip'), t('reel.plate.seconds', { n: item.seconds })]] : []),
                    [t('reel.plate.cost'), [item.studio === 'quick' ? null : freeLabel(costs.data?.[item.studio], t), priceOf(item)].filter(Boolean).join(' · ') || '—'],
                  ] as Array<[string, string]>).map(([k, v]) => (
                    <div key={k} className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
                      <dt className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{k}</dt>
                      <dd className="font-semibold tabular-nums">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 text-sm text-muted-foreground">{t('reel.details.real')}</p>
                <button
                  type="button"
                  onClick={() => navigate({ pathname: location.pathname, search: location.search, hash: '#make' }, { replace: true, state: location.state })}
                  className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-accent font-bold text-[#0b1416]"
                >
                  <Sparkles className="h-4 w-4" aria-hidden /> {t('reel.make')}
                </button>
              </div>
            ) : (
              <ShareCard studio={item.studio} itemId={item.id} mediaUrl={item.kind === 'video' ? item.src : undefined} fileName={`${item.id}.mp4`} compact />
            )}
          </div>
        </div>
      , document.body)}
    </div>
  );
}
